import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  uploadPartnerApplicationDocument,
  storePhotosSchema,
  type OfflineStoreData,
  type StoreData,
  type StorePhotosFormValues,
} from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_STORES,
} from '~domains/partner-application/shared/constants';
import { getApplicationEventProperties } from '~domains/partner-application/shared/lib';

import { ampli } from '~entities/ampli';

import { FileUpload, type FileUploadRef } from '~shared/ui/file-upload/rhf';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { toast } from '@breezeventures/ui-kit/toast';
import { Typography } from '@breezeventures/ui-kit/typography';
import { zodResolver } from '@hookform/resolvers/zod';

type StorePhotosProps = {
  applicationId?: string;
  storeDraft?: Partial<StoreData> | null;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (data: StorePhotosFormValues) => Promise<void>;
};

export const StorePhotos = ({
  applicationId,
  storeDraft,
  onBack,
  onClose,
  onSubmit,
}: StorePhotosProps) => {
  const storePhotosFileUploadRef = useRef<FileUploadRef>(null);
  const offlineStoreDraft = storeDraft as OfflineStoreData | null;

  const methods = useForm<StorePhotosFormValues>({
    resolver: zodResolver(storePhotosSchema),
    mode: 'all',
    defaultValues: offlineStoreDraft?.photos ?? {
      STORE_AND_MERCHANDISE_DISPLAY: undefined,
    },
  });

  useEffect(() => {
    if (offlineStoreDraft?.photos) {
      methods.trigger();
    }
  }, [offlineStoreDraft?.photos, methods]);

  const handleSubmit = async (data: StorePhotosFormValues) => {
    if (storePhotosFileUploadRef.current?.checkIsUploading()) {
      toast('Still uploading, boss!', { type: 'info' });
      return;
    }
    await onSubmit(data);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_STORES}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <Typography.Title size="s" className="py-6">
          Upload photos of your store and product display
        </Typography.Title>
        <div className="flex flex-col gap-3">
          <Controller
            name="STORE_AND_MERCHANDISE_DISPLAY"
            control={methods.control}
            render={(props) => (
              <FileUpload
                ref={storePhotosFileUploadRef}
                {...props}
                title="Store and merchandise display photos"
                options={{
                  allowedExtensions: ['jpg', 'jpeg', 'png'],
                  maxSize: 10 * 1024 * 1024, // 10MB
                  multiple: true,
                }}
                onUpload={async (req, opts) => {
                  const result = await uploadPartnerApplicationDocument(
                    req,
                    opts
                  );
                  ampli.mpPaOfflineStorePhotoUploaded(
                    getApplicationEventProperties(applicationId)
                  );
                  return result;
                }}
              />
            )}
          />
        </div>
      </Screen>
    </form>
  );
};
